import React, { useState, useMemo } from "react";
import { TableCell, TableRow } from "../../../design/table";
import moment from "moment-timezone";
import { Event } from "@visitproof/types";
import EventStatusBadge from "../../../components/EventStatusBadge";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../design/dialog";
import { Text } from "../../../design/text";
import { Button } from "../../../design/button";
import ImageThumbnail from "./ImageThumbnail";
import LocationImageView from "../../../components/LocationImageView";
import { Divider } from "../../../design/divider";
import {
  ClockIcon,
  MapPinIcon,
  PhotoIcon,
  DocumentTextIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { Badge } from "../../../design/badge";
import { useVisit } from "../../../contexts/VisitContext";
import { useAuth } from "../../../contexts/AuthContext";
import EditEventDialog from "./EditEventDialog";

interface VisitEventCellProps {
  event: Event;
}

const VisitEventCell: React.FC<VisitEventCellProps> = ({ event }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { selectedVisit, setSelectedVisit } = useVisit();
  const { isAdmin, claims } = useAuth();

  const imageCount = event.images?.length ?? 0;

  // Check if user is admin or owner
  const canEdit = isAdmin || claims?.role === "owner";

  // Use the visit's timezone for consistent date display with PDF
  // Priority: visit.timeZoneId > location.timeZoneId > browser timezone
  const timeZone = useMemo(() => {
    return selectedVisit?.timeZoneId || selectedVisit?.location?.timeZoneId || moment.tz.guess();
  }, [selectedVisit?.timeZoneId, selectedVisit?.location?.timeZoneId]);

  // Format event date/time using the visit's timezone
  const formatEventDate = (date: Date, format: string) => {
    return moment(date).tz(timeZone).format(format);
  };

  const handleEventUpdated = () => {
    setIsEditOpen(false);
    // Refresh the visit data by re-selecting the current visit
    if (selectedVisit?.id) {
      setSelectedVisit(selectedVisit.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
  };

  return (
    <>
      <TableRow
        key={event.id}
        title={`View event details`}
        onClick={() => setIsOpen(true)}
        className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
      >
        <TableCell>
          <div className="flex flex-col">
            <Text className="font-medium text-zinc-900 dark:text-zinc-100">
              {event.createdAt
                ? formatEventDate(event.createdAt.toDate(), "MMM D, YYYY")
                : "-"}
            </Text>
            <Text className="text-xs text-zinc-500 dark:text-zinc-400">
              {event.createdAt
                ? formatEventDate(event.createdAt.toDate(), "h:mm A")
                : ""}
            </Text>
          </div>
        </TableCell>
        <TableCell>
          <EventStatusBadge type={event.type} severity={event.severity} />
        </TableCell>
        <TableCell>
          <Text className="max-w-md text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
            {event.body || "-"}
          </Text>
        </TableCell>
        <TableCell>
          {imageCount > 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {event.images?.slice(0, 3).map((image, index) => (
                  <div
                    key={image || index}
                    className="relative h-8 w-8 overflow-hidden rounded-md border-2 border-white dark:border-zinc-900"
                  >
                    <ImageThumbnail path={image} />
                  </div>
                ))}
              </div>
              {imageCount > 3 && (
                <Badge color="zinc">+{imageCount - 3}</Badge>
              )}
            </div>
          ) : (
            <Text className="text-sm text-zinc-400">-</Text>
          )}
        </TableCell>
      </TableRow>

      {/* Event Detail Dialog */}
      {isOpen && !isEditOpen && (
        <Dialog open={isOpen} onClose={setIsOpen} size="lg">
          <DialogTitle>Event Details</DialogTitle>
          <DialogDescription>
            Recorded on{" "}
            {event.createdAt
              ? formatEventDate(event.createdAt.toDate(), "MMMM D, YYYY [at] h:mm A")
              : "Unknown date"}
          </DialogDescription>
          <DialogBody className="space-y-6">
            {/* Event Type and Time */}
            <div className="flex flex-wrap items-center gap-3">
              <EventStatusBadge type={event.type} severity={event.severity} />
              <div className="flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                <ClockIcon className="h-4 w-4" />
                {event.createdAt
                  ? formatEventDate(event.createdAt.toDate(), "h:mm:ss A")
                  : "-"}
              </div>
            </div>

            <Divider />

            {/* Event Body */}
            {event.body && (
              <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div className="mb-2 flex items-center gap-2">
                  <DocumentTextIcon className="h-4 w-4 text-zinc-500" />
                  <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Details
                  </Text>
                </div>
                <Text className="whitespace-pre-wrap text-zinc-900 dark:text-zinc-100">
                  {event.body}
                </Text>
              </div>
            )}

            {/* Location */}
            {event.location && (
              <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div className="mb-3 flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 text-zinc-500" />
                  <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Location
                  </Text>
                </div>
                <LocationImageView location={event.location} />
              </div>
            )}

            {/* Images */}
            {event.images && event.images.length > 0 && (
              <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
                <div className="mb-3 flex items-center gap-2">
                  <PhotoIcon className="h-4 w-4 text-zinc-500" />
                  <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Photos ({event.images.length})
                  </Text>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {event.images.map((image, index) => (
                    <div
                      key={image || index}
                      className="aspect-square overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-700"
                    >
                      <ImageThumbnail path={image} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogActions>
            {canEdit && (
              <Button outline onClick={handleEdit}>
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Event
              </Button>
            )}
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Event Dialog */}
      {isEditOpen && (
        <EditEventDialog
          event={event}
          isOpen={isEditOpen}
          onClose={handleCloseEdit}
          onEventUpdated={handleEventUpdated}
        />
      )}
    </>
  );
};

export default VisitEventCell;
